import type { Brain } from '@/types/brain'
import type { SpatialData } from '@/types/spatialData'
import type { Voice } from '@/types/voice'

import * as Sentry from '@sentry/vue'
import axios from 'axios'
import { defu } from 'defu'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import router from '@/router'
import { defaultBrain } from '@/types/brain'
import { defaultVoice } from '@/types/voice'

export interface Charecter {
  id: number | null
  name: string
  description: string
  extra_info: string
  voice: Voice
  brain: Brain
  resource: string
  first_greeting: string
  greeting: string
  usage_count: number
  status: string
  created_at: Date
  updated_at: Date
  spatialData: SpatialData
  driven_base_url: string
  model_settings_json: string
}

const axiosInstance = axios.create({
  // @ts-ignore
  baseURL: import.meta.env.VITE_API_SERVER,
  withCredentials: true,
})

axiosInstance.interceptors.response.use((response) => {
  return response
}, (error) => {
  if (error.response.status === 401) {
    router.push('/login')
  }
  return Promise.reject(error)
})

export const useCharecterStore = defineStore('charecter', () => {
  const charecterList = ref<Charecter[]>([])
  const currentCharecter = ref<Charecter>({
    id: null,
    name: '',
    description: '',
    extra_info: '{}',
    voice: defaultVoice,
    brain: defaultBrain,
    status: '0',
    resource: '',
    first_greeting: '',
    greeting: '',
    usage_count: 0,
    created_at: new Date(),
    updated_at: new Date(),
    driven_base_url: '',
    spatialData: {
      shape_numpy: '',
      style_numpy: '',
      model_settings: '',
    },
    model_settings_json: '{}',
  })

  const setCurrentCharecter = (charecterId: number | null) => {
    currentCharecter.value = charecterList.value.find((charecter: Charecter) => charecter.id === charecterId)!
    currentCharecter.value.spatialData = {
      shape_numpy: '',
      style_numpy: '',
      model_settings: '',
    }

    if (charecterId) {
      router.push(`/charecter`)
    }
    else {
      router.push(`/`)
    }
  }

  watch(() => currentCharecter.value.spatialData.model_settings, (newVal) => {
    currentCharecter.value.model_settings_json = atob(newVal)
  })

  const addCharecter = (charecter: Charecter) => {
    charecterList.value.push(charecter)
  }

  // 登录
  const login = async (username: string, password: string) => {
    const response = await axiosInstance.post('/admin/login', {
      username,
      password,
    })
    if (response.data?.code === 0) {
      router.push('/')
      return null
    }
    return response.data
  }

  // 拉取charecter列表
  const fetchCharecters = async () => {
    Sentry.startNewTrace(() => {
      Sentry.startSpan(
        { op: 'ui.interaction.click', name: 'fetch click' },
        async () => {
          // const a = await axiosInstance.get('/character/test')
          // console.log(a)
          const response = await axiosInstance.get('/admin/character_template/list')
          if (response.status === 200 && response.data.code === 0) {
            response.data.data.forEach((charecter: Charecter) => {
              charecter.extra_info = JSON.stringify(charecter.extra_info)
            })
            charecterList.value = response.data.data
            return null
          }
          else {
            return response.data.message
          }
        },
      )
    })
  }

  // 提交更新charecter
  const updateCharecter = async (charecter: Charecter) => {
    // 使用 defu 创建一个新对象
    const newCharecter = defu({}, charecter)

    // 检查 extra_info 是否为字符串，如果是则解析，否则直接使用
    newCharecter.extra_info = typeof charecter.extra_info === 'string'
      ? JSON.parse(charecter.extra_info)
      : charecter.extra_info

    // newCharecter.spatialData.model_settings = btoa(JSON.stringify(newCharecter.spatialData.model_settings_json))

    newCharecter.spatialData.model_settings = ''
    if (typeof newCharecter.model_settings_json === 'string') {
      newCharecter.model_settings_json = JSON.parse(newCharecter.model_settings_json)
    }

    console.log(newCharecter)

    const response = await axiosInstance.post('/admin/character_template/update', newCharecter)
    if (response.data.code === 0) {
      return null
    }
    else {
      return response.data.message
    }
  }

  // 创建charecter
  const createCharecter = async () => {
    const response = await axiosInstance.post('/admin/character_template/create')
    if (response.data.code === 0) {
      const data = response.data.data
      addCharecter(data)
      setCurrentCharecter(data.id)
    }
  }

  // 创建voice
  const createVoice = async (params: CreateVoiceParams) => {
    const response = await axiosInstance.post('/admin/create_voice', params)
    if (response.data.code === 0) {
      return null
    }
    else {
      return response.data.message
    }
  }

  const env: keyof typeof environments = localStorage.getItem('env') as keyof typeof environments || 'local'

  const environments = {
    local: {
      label: '本地',
      key: 'local',
      url: 'http://localhost:4202',
    },
    test:
    {
      label: '测试（中国）',
      key: 'test',
      url: 'https://3d-dev.spatialwalk.xyz',
    },
    usa: {
      label: '美国',
      key: 'usa',
      url: 'https://spatialwalk-usa.zeabur.app',
    },
  }

  const currentEnvironment = ref<keyof typeof environments>(env)
  axiosInstance.defaults.baseURL = environments[currentEnvironment.value].url
  const changeEnvironment = (environment: keyof typeof environments) => {
    currentEnvironment.value = environment
    localStorage.setItem('env', environment)
    window.location.reload()
  }

  return {
    environments,
    environment: currentEnvironment,
    changeEnvironment,
    charecterList,
    currentCharecter,
    setCurrentCharecter,
    addCharecter,
    fetchCharecters,
    createCharecter,
    login,
    updateCharecter,
    createVoice,
  }
})

export interface CreateVoiceParams {
  speaker_id: string
  audio: {
    bytes: string
    format: string
    text: string
  }
  language: number
}
